/**
 * Ob sich der Entwurf der Zielpflege gegenüber dem GESPEICHERTEN Stand
 * überhaupt geändert hat — eigene, reine Funktion statt Vergleich im
 * Klick-Handler (Muster wie `planAnzeige.ts` und `freigabeKnopf.ts`).
 *
 * Der Zweck ist ein anderer als bei `zeigePlan`: Dort geht es um „weicht das
 * heutige Ziel vom eingefrorenen Plan ab", hier um „habe ich in diesem
 * Formular überhaupt etwas angefasst". Deshalb wird gegen den gespeicherten
 * Stand verglichen (`saved` aus `useSegmentTargets`), nicht gegen den Plan:
 * Bei einer eingefrorenen Runde soll ein Klick auf „Speichern" ohne jede
 * Änderung keinen Begründungsdialog aufreißen — sonst landet eine
 * bedeutungslose Begründung in genau der Akte, die glaubwürdig sein soll.
 *
 * Zwei Fallstricke, gegen die diese Funktion testet:
 *  - Eingabefelder liefern `number | string`, ein geleertes Feld `""`. `""`
 *    gegen `null` ist KEINE Änderung — beides heißt „nichts eingetragen".
 *  - Die Umsatzsteigerung wird nie aus der Datenbank übernommen, sondern
 *    stets aus Zielumsatz und Vorjahresumsatz abgeleitet (`wachstumProzent`,
 *    s. dort — die gespeicherte Spalte driftet sonst ab). Der Aufrufer muss
 *    den „gespeicherten" Wachstumswert also genauso ableiten und hier
 *    hineingeben, sonst würde ein längst veralteter DB-Wert eine Änderung
 *    vortäuschen, die keine ist.
 */
export type ZielWerte = {
  jahresziel: number | string | null | undefined;
  wachstum: number | string | null | undefined;
  deckungsbeitrag: number | string | null | undefined;
  krankheitstage: number | string | null | undefined;
  /** Genau zwölf Einträge, ein Monat pro Index. */
  monatsUmsatz: ReadonlyArray<number | string | null | undefined>;
  /** Genau zwölf Einträge, ein Monat pro Index. */
  monatsStunden: ReadonlyArray<number | string | null | undefined>;
};

/** `""` und `null`/`undefined` gelten als derselbe „nichts eingetragen"-Wert. */
function gleich(
  a: number | string | null | undefined,
  b: number | string | null | undefined,
): boolean {
  const norm = (v: number | string | null | undefined) =>
    v === "" || v == null ? null : Number(v);
  return norm(a) === norm(b);
}

export function hatAenderungen(
  gespeichert: ZielWerte,
  entwurf: ZielWerte,
): boolean {
  if (!gleich(gespeichert.jahresziel, entwurf.jahresziel)) return true;
  if (!gleich(gespeichert.wachstum, entwurf.wachstum)) return true;
  if (!gleich(gespeichert.deckungsbeitrag, entwurf.deckungsbeitrag)) return true;
  if (!gleich(gespeichert.krankheitstage, entwurf.krankheitstage)) return true;

  const monate = Math.max(
    gespeichert.monatsUmsatz.length,
    entwurf.monatsUmsatz.length,
    gespeichert.monatsStunden.length,
    entwurf.monatsStunden.length,
  );
  for (let i = 0; i < monate; i++) {
    if (!gleich(gespeichert.monatsUmsatz[i], entwurf.monatsUmsatz[i])) return true;
    if (!gleich(gespeichert.monatsStunden[i], entwurf.monatsStunden[i])) return true;
  }
  return false;
}
